// Уведомления: подписки клиента, рейсы, «Входящие» (история уведомлений), каналы (Telegram, push), проверка цен,
// окно «Следить за ценой»
import type { AlertView, AlertsList, FlightWatchView, InboxItem, InboxList, NotifyChannels } from '@sayohat/shared';
import { api } from '../../shared/lib/api';
import { bus } from '../../shared/lib/bus';
import { getConfig } from '../../shared/lib/config';
import { clientId } from '../../shared/lib/storage';
import { createStore } from '../../shared/lib/store';
import { track } from '../../shared/lib/track';
import type { WatchRequest } from '../../shared/lib/types';
import { toast } from '../../shared/ui/Toast';
import { b64ToBytes, canWatch } from './model';

export type PushState =
  | { state: 'checking' }
  | { state: 'unsupported'; text: string }
  | { state: 'denied' }
  | { state: 'on' }
  | { state: 'off' };

export interface AlertsState {
  loaded: boolean;
  error: string;
  alerts: AlertView[];
  channels: (NotifyChannels & { telegramAvailable?: boolean; telegramBot?: string | null }) | null;
  /** ждём «Запустить» в боте */
  tgWaiting: boolean;
  push: PushState;
  testResult: string;
  checking: boolean;
  /** история уведомлений этого браузера (новые сверху) */
  inbox: InboxItem[];
  unread: number;
  /** рейсы, за которыми следим в браузере */
  watches: FlightWatchView[];
}

export const alerts = createStore<AlertsState>({
  loaded: false, error: '', alerts: [], channels: null, tgWaiting: false, push: { state: 'checking' }, testResult: '', checking: false,
  inbox: [], unread: 0, watches: [],
});

/** Значок раздела: действующие подписки на цены и рейсы */
const emitCount = () => {
  const s = alerts.get();
  bus.emit('alerts:changed', s.alerts.filter((a) => !a.expired).length + s.watches.length);
};

export async function load(): Promise<void> {
  // «Входящие» и рейсы не мешают подпискам: их ошибка — пустой список
  const [data, box, fw] = await Promise.all([
    api<AlertsList>(`/api/alerts?client=${clientId}`).catch((err: Error) => err),
    api<InboxList>(`/api/inbox?client=${clientId}`).catch(() => null),
    api<{ watches: FlightWatchView[] }>(`/api/flight-watch?client=${clientId}`).catch(() => null),
  ]);
  if (box) alerts.set({ inbox: box.items, unread: box.unread });
  if (fw) alerts.set({ watches: fw.watches });
  if (data instanceof Error) {
    alerts.set({ loaded: true, error: data.message });
    return;
  }
  alerts.set({ loaded: true, error: '', alerts: data.alerts, channels: data.channels, tgWaiting: false });
  emitCount();
  refreshPush();
}

// ---------- «Входящие» и рейсы ----------

/** Отметить прочитанными всё во «Входящих» */
export async function readInbox(): Promise<void> {
  if (!alerts.get().unread) return;
  const box = await api<InboxList>('/api/inbox/read', { method: 'POST', body: { client: clientId } }).catch(() => null);
  if (box) alerts.set({ inbox: box.items, unread: box.unread });
}

export async function removeWatch(id: string): Promise<void> {
  await api(`/api/flight-watch?client=${clientId}&id=${encodeURIComponent(id)}`, { method: 'DELETE' }).catch((e: Error) => toast(e.message));
  alerts.set({ watches: alerts.get().watches.filter((w) => w.id !== id) });
  emitCount();
}

// ---------- Каналы ----------

// Пока подписка браузера отправляется на сервер, статус «Включены» не показываем — иначе можно нажать «Тест» раньше,
// чем сервер узнает о подписке
let pushSaving = false;

const pushSupported = () => typeof navigator !== 'undefined' && 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;

export async function refreshPush(): Promise<void> {
  if (!pushSupported()) {
    alerts.set({ push: { state: 'unsupported', text: window.isSecureContext
      ? 'Этот браузер не поддерживает уведомления. На iPhone: добавьте сайт на экран «Домой» и откройте оттуда.'
      : 'Уведомления в браузере работают только на сайте с HTTPS.' } });
    return;
  }
  if (Notification.permission === 'denied') { alerts.set({ push: { state: 'denied' } }); return; }
  try {
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    if (pushSaving) return;
    const on = Boolean(sub && Notification.permission === 'granted');
    // Браузер подписан, а сервер о подписке не знает (новая база, другой сервер) — отправляем подписку заново
    const channels = alerts.get().channels;
    if (on && sub && channels && !channels.pushCount) {
      pushSaving = true;
      try {
        await api('/api/push/subscribe', { method: 'POST', body: { client: clientId, subscription: sub.toJSON() } });
        const data = await api<AlertsList>(`/api/alerts?client=${clientId}`).catch(() => null);
        if (data) alerts.set({ channels: data.channels });
      } catch {
        // Не получилось — покажем «выключены», пользователь включит заново
        pushSaving = false;
        alerts.set({ push: { state: 'off' } });
        return;
      }
      pushSaving = false;
    }
    alerts.set({ push: { state: on ? 'on' : 'off' } });
  } catch {
    alerts.set({ push: { state: 'off' } });
  }
}

let tgPoll: ReturnType<typeof setInterval> | undefined;

export async function linkTelegram(): Promise<void> {
  // Окно открываем сразу (иначе браузер заблокирует всплывающее окно), адрес подставляем после ответа сервера
  const win = window.open('', '_blank');
  try {
    const { url } = await api<{ url: string }>('/api/telegram/link', { method: 'POST', body: { client: clientId } });
    if (win) win.location.href = url; else location.href = url;
    alerts.set({ tgWaiting: true });
    clearInterval(tgPoll);
    let tries = 0;
    tgPoll = setInterval(async () => {
      if (++tries > 100) { clearInterval(tgPoll); return; }
      const data = await api<AlertsList>(`/api/alerts?client=${clientId}`).catch(() => null);
      if (data && data.channels.telegramLinked) {
        clearInterval(tgPoll);
        alerts.set({ channels: data.channels, tgWaiting: false });
        toast('Telegram подключён');
      }
    }, 3000);
  } catch (err) {
    if (win) win.close();
    toast((err as Error).message);
  }
}

export async function unlinkTelegram(): Promise<void> {
  await api('/api/telegram/unlink', { method: 'POST', body: { client: clientId } }).catch((e: Error) => toast(e.message));
  load();
}

export async function enablePush(): Promise<void> {
  pushSaving = true;
  try {
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') { toast('Уведомления не разрешены'); return; }
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: b64ToBytes(getConfig().pushKey || '') });
    await api('/api/push/subscribe', { method: 'POST', body: { client: clientId, subscription: sub.toJSON() } });
    toast('Уведомления в браузере включены');
  } catch (err) {
    toast(`Не удалось включить: ${(err as Error).message}`);
  }
  pushSaving = false;
  load();
}

export async function disablePush(): Promise<void> {
  const reg = await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.getSubscription();
  if (sub) {
    await api('/api/push/unsubscribe', { method: 'POST', body: { client: clientId, endpoint: sub.endpoint } }).catch(() => {});
    await sub.unsubscribe();
  }
  load();
}

export async function testNotify(): Promise<void> {
  alerts.set({ testResult: 'Отправляем…' });
  try {
    const r = await api<{ delivered: number; errors: string[] }>('/api/alerts/test', { method: 'POST', body: { client: clientId } });
    alerts.set({ testResult: r.delivered ? `Отправлено (${r.delivered})` : `Не доставлено: ${r.errors.join('; ')}` });
  } catch (err) {
    alerts.set({ testResult: (err as Error).message });
  }
}

// ---------- Подписки ----------

export async function checkNow(): Promise<void> {
  alerts.set({ checking: true });
  try {
    const list = await api<AlertView[]>('/api/alerts/check', { method: 'POST', body: { client: clientId } });
    alerts.set({ alerts: list });
    toast('Цены обновлены');
  } catch (err) {
    toast((err as Error).message);
  } finally {
    alerts.set({ checking: false });
  }
}

export async function remove(id: string): Promise<void> {
  await api(`/api/alerts?client=${clientId}&id=${encodeURIComponent(id)}`, { method: 'DELETE' }).catch((e: Error) => toast(e.message));
  load();
}

// ---------- Окно «Следить за ценой» ----------

export interface WatchState {
  open: boolean;
  query: WatchRequest | null;
  max: string;
  saving: boolean;
  /** итог подписки: текст и нужна ли подсказка про каналы */
  result: { text: string; hint: boolean } | null;
  done: boolean;
}

export const watch = createStore<WatchState>({ open: false, query: null, max: '', saving: false, result: null, done: false });

export function openWatch(query: WatchRequest | null | undefined): void {
  if (!canWatch(query)) { toast('Сначала найдите билеты'); return; }
  watch.replace({ open: true, query, max: '', saving: false, result: null, done: false });
}

export const closeWatch = () => watch.set({ open: false });

export async function saveWatch(): Promise<void> {
  const { query: q } = watch.get();
  if (!q) return;
  // Значение поля читаем из DOM: его могут заполнить и внешние скрипты
  const input = document.getElementById('watch-max') as HTMLInputElement | null;
  const max = input ? input.value : watch.get().max;
  watch.set({ saving: true, result: { text: 'Подписываем…', hint: false } });
  try {
    const sub = await api<AlertView>('/api/alerts', {
      method: 'POST',
      body: {
        client: clientId,
        kind: q.kind || 'flight', trainNumber: q.trainNumber || null, trainBrand: q.trainBrand || null,
        origin: q.origin, destination: q.destination, originName: q.originName, destinationName: q.destinationName, place: q.place,
        departure: q.departure, ret: q.ret, direct: q.direct, currency: q.currency,
        adults: q.adults, children: q.children, infants: q.infants,
        maxPrice: max,
      },
    });
    track('subscribe', { kind: q.kind || 'flight' });
    await load();
    const ch = alerts.get().channels;
    const linked = Boolean(ch && (ch.telegramLinked || ch.pushCount));
    const text = `Готово! ${sub.lastPrice === null
      ? (q.kind === 'train' ? 'Сейчас мест нет — сообщим, когда появятся.' : 'Сейчас билетов нет — сообщим, когда появятся.')
      : `Сейчас от ${sub.lastPriceText} — сообщим, когда станет дешевле.`}`;
    watch.set({ saving: false, done: true, result: { text, hint: !linked } });
  } catch (err) {
    watch.set({ saving: false, result: { text: (err as Error).message, hint: false } });
  }
}
